import { useEffect, useState } from "react";
import { formatIst, formatDateOnly, todayIstIso } from "@/lib/tz";
import { EDIT_WINDOW_HOURS } from "@/lib/editWindow";

/**
 * Read-only date display (IST).
 *
 * - Default (new submissions): shows "Today: <today IST>".
 * - Editing existing row: pass `editingDate` (yyyy-MM-dd) and `createdAt`
 *   (timestamptz). Shows the original task date plus the remaining 72-hour
 *   edit window. After 72h the DB trigger blocks updates anyway.
 */
export function todayIso() {
  return todayIstIso();
}

interface Props {
  label?: string;
  editingDate?: string | null;
  createdAt?: string | Date | null;
  /** Override the edit window length (hours). Defaults to EDIT_WINDOW_HOURS (72). */
  windowHours?: number;
  /** Override the label shown for the window (e.g. "72 hours" or "7 days"). */
  windowLabel?: string;
}

export function LockedDateField({
  label = "DATE",
  editingDate,
  createdAt,
  windowHours,
  windowLabel,
}: Props) {
  const [now, setNow] = useState(() => new Date());
  const winHours = windowHours ?? EDIT_WINDOW_HOURS;
  const winLabel = windowLabel ?? `${winHours} hours`;

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (editingDate) {
    const display = formatDateOnly(editingDate, "EEE, dd MMM yyyy");
    let remainingText = "";
    let expired = false;
    if (createdAt) {
      const created = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
      const remainingMs =
        winHours * 3600_000 - (now.getTime() - created.getTime());
      if (remainingMs <= 0) {
        expired = true;
        remainingText = `Edit window closed (over ${winLabel} since creation).`;
      } else {
        const h = Math.floor(remainingMs / 3600_000);
        const m = Math.floor((remainingMs % 3600_000) / 60_000);
        remainingText = `Editable for ${h}h ${m}m more (${winLabel} window).`;
      }
    }

    return (
      <div className="space-y-1">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground">
          <span>{display} IST</span>
        </div>
        {remainingText && (
          <div
            className={
              "text-[10px] " + (expired ? "text-destructive" : "text-muted-foreground")
            }
          >
            {remainingText}
          </div>
        )}
      </div>
    );
  }

  const display = formatIst(now, "EEE, dd MMM yyyy");
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground">
        <span>Today: {display} IST</span>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Auto-set to today. Backdating and future-dating are disabled.
      </div>
    </div>
  );
}
